import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, LayoutGrid, Inbox, Globe, Users, Plus, Bell, X, FileBarChart, KeyRound, Menu } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { UI_TIMEOUTS, devError } from '../lib/constants';
import ChangePasswordDialog from './ChangePasswordDialog';

export default function Layout({ children, onNewTask }) {
  const { user, workspace, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const loadNotifs = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifs(data);
    } catch (err) {
      devError('failed to load notifications', err);
    }
  }, []);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, UI_TIMEOUTS.NOTIFICATION_POLL_MS);
    return () => clearInterval(t);
  }, [loadNotifs]);

  const unread = notifs.filter(n => !n.read).length;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const markAllRead = async () => {
    await api.post('/notifications/read-all');
    loadNotifs();
  };

  const openNotif = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
      } catch (err) {
        devError('failed to mark notification read', err);
      }
    }
    setNotifOpen(false);
    if (n.task_id) navigate(`/tasks/${n.task_id}`);
    loadNotifs();
  };

  const navItems = [
    { to: '/', label: 'ALL TASKS', icon: LayoutGrid, testid: 'nav-all' },
    { to: '/mine', label: 'MY TASKS', icon: Inbox, testid: 'nav-mine' },
    { to: '/general', label: 'GENERAL', icon: Globe, testid: 'nav-general' },
  ];
  if (user?.role === 'boss' || user?.role === 'manager') {
    navItems.push({ to: '/created', label: 'CREATED BY ME', icon: Plus, testid: 'nav-created' });
    navItems.push({ to: '/snapshot', label: 'SNAPSHOT', icon: FileBarChart, testid: 'nav-snapshot' });
    navItems.push({ to: '/users', label: 'PEOPLE', icon: Users, testid: 'nav-users' });
  }

  return (
    <div className="min-h-screen flex bg-[#F4F4F5] text-zinc-900">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-zinc-200 bg-white">
        <div className="p-6 border-b border-zinc-200">
          <div className="font-display font-black text-2xl tracking-tighter leading-none">SYNC/GRID</div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mt-1 truncate" title={workspace?.name}>
            {workspace?.name || 'TASK CONTROL'}
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`flex items-center gap-3 px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] border transition-colors ${active ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'border-transparent text-zinc-700 hover:bg-zinc-100'}`}
              >
                <Icon size={16} strokeWidth={1.8} /> {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-zinc-200">
          <div className="mb-3">
            <div className="font-semibold text-sm truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{user?.role}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={() => setPwdOpen(true)}
            data-testid="open-change-pwd"
            className="w-full mb-2 flex items-center justify-center gap-2 border border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:border-zinc-900 transition-colors"
          >
            <KeyRound size={14} strokeWidth={1.8} /> Change password
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white transition-colors"
          >
            <LogOut size={14} strokeWidth={1.8} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center justify-between border-b border-zinc-200 bg-white px-4 md:px-8 py-4">
          <div className="md:hidden font-display font-black text-xl">SYNC/GRID</div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            {(user?.role === 'boss' || user?.role === 'manager') && (
              <button
                onClick={onNewTask}
                data-testid="new-task-btn"
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#002088] transition-colors"
              >
                <Plus size={14} strokeWidth={2} /> New Task
              </button>
            )}
            <div className="relative">
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                data-testid="notif-btn"
                className="relative border border-zinc-900 p-2 hover:bg-zinc-900 hover:text-white transition-colors"
              >
                <Bell size={16} strokeWidth={1.8} />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#002FA7] text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notif-count">
                    {unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-zinc-900 shadow-brutal z-50" data-testid="notif-panel">
                  <div className="flex items-center justify-between border-b border-zinc-200 p-3">
                    <div className="text-xs font-bold uppercase tracking-[0.15em]">Notifications</div>
                    <div className="flex items-center gap-1">
                      <button onClick={markAllRead} className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-900" data-testid="notif-read-all">Mark all read</button>
                      <button onClick={() => setNotifOpen(false)}><X size={14} /></button>
                    </div>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifs.length === 0 && <div className="p-4 text-sm text-zinc-500">No notifications yet.</div>}
                    {notifs.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => openNotif(n)}
                        data-testid={`notif-item-${n.id}`}
                        className={`w-full text-left p-3 border-b border-zinc-100 hover:bg-zinc-50 ${!n.read ? 'bg-blue-50/40' : ''}`}
                      >
                        <div className="text-sm">{n.message}</div>
                        <div className="text-[10px] font-mono text-zinc-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8">{children}</main>
      </div>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <div className="w-72 max-w-[85vw] bg-white border-l border-zinc-900 flex flex-col">
            <div className="p-5 border-b border-zinc-200 flex items-start justify-between">
              <div>
                <div className="font-display font-black text-2xl tracking-tighter leading-none">SYNC/GRID</div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mt-1 truncate" title={workspace?.name}>
                  {workspace?.name || 'TASK CONTROL'}
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} data-testid="close-mobile-drawer" className="p-1">
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`${item.testid}-mobile`}
                    className={`flex items-center gap-3 px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] border ${active ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'border-transparent text-zinc-700 hover:bg-zinc-100'}`}
                  >
                    <Icon size={16} strokeWidth={1.8} /> {item.label}
                  </Link>
                );
              })}
            </nav>
            <div className="p-4 border-t border-zinc-200">
              <div className="mb-3">
                <div className="font-semibold text-sm truncate">{user?.name}</div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{user?.role}</div>
                <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
              </div>
              <button
                onClick={() => { setMobileMenuOpen(false); setPwdOpen(true); }}
                data-testid="open-change-pwd-mobile"
                className="w-full mb-2 flex items-center justify-center gap-2 border border-zinc-300 px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:border-zinc-900"
              >
                <KeyRound size={14} strokeWidth={1.8} /> Change password
              </button>
              <button
                onClick={async () => { setMobileMenuOpen(false); await handleLogout(); }}
                data-testid="logout-btn-mobile"
                className="w-full flex items-center justify-center gap-2 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white"
              >
                <LogOut size={14} strokeWidth={1.8} /> Logout
              </button>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </div>
  );
}
